import * as React from 'react';
import { styled } from 'linaria/react';
import { css } from 'linaria';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import { constructGallery } from '@jetshop/ui/Gallery/constructGallery';
import Image from '@jetshop/ui/Image/Image';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import { ReactComponent as Play } from '../../../svg/Play.svg';
import { VimeoVideo } from './VimeoVideo';

var __rest =
  (this && this.__rest) ||
  function(s, e) {
    var t = {};
    for (var p in s)
      if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === 'function')
      for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
        if (
          e.indexOf(p[i]) < 0 &&
          Object.prototype.propertyIsEnumerable.call(s, p[i])
        )
          t[p[i]] = s[p[i]];
      }
    return t;
  };

const wrapperStyles = css`
  position: relative;
  img {
    max-height: 100vh;
  }
  .image-gallery {
    z-index: 140;
  }
  .image-gallery-image {
    text-align: center;
  }
  .image-gallery-thumbnail-inner {
    font-size: 10px;
    &.video-thumb {
      img {
        opacity: 0.2 !important;
      }
    }
  }
  .image-gallery-right-nav,
  .image-gallery-left-nav,
  .image-gallery-fullscreen-button {
    z-index: 2;
    font-size: 2rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    height: 2.5rem;
    width: 2.5rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    transition: background 0.3s ease-in-out;
    margin: 0 0.5rem;
    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
    &:before {
      color: black;
      text-shadow: none;
    }
  }
  .image-gallery-fullscreen-button {
    margin-bottom: 0.5rem;
  }
  .image-gallery-fullscreen-button:before {
    text-shadow: none;
    font-size: 0.7em;
    padding: 0;
  }
  .image-gallery-thumbnail.active {
    border-width: 1px;
  }
  .image-gallery-thumbnails-wrapper.left .image-gallery-thumbnails,
  .image-gallery-thumbnails-wrapper.right .image-gallery-thumbnails {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .fullscreen {
    .image-gallery-fullscreen-button {
      margin: 2rem;
    }
    .image-gallery-right-nav,
    .image-gallery-left-nav {
      margin: 0 2rem;
    }
    img {
      width: auto;
      max-width: 100%;
    }
    .image-gallery-thumbnails-wrapper {
      display: none;
    }
  }
  .image-gallery-slide {
    background-color: var(--slide-bg);
  }
`;
const Overlay = styled('div')`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const VideoIcon = styled('div')`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  margin: auto;
  text-align: center;
  z-index: 1;
  svg {
    width: 40px !important;
    height: 40px !important;
    color: black;
  }
`;
export const Gallery = props => {
  const {
      sizes = [1, 1, 1, 1 / 2, 1 / 2],
      fullScreenSizes = [1, 1, 1, 1],
      thumbnailSizes = ['5rem'],
      aspect = '1:1',
      thumbnailPosition = 'bottom',
      backgroundColor = 'white',
      thumbnailQuality,
      quality,
      images,
      galleryRef,
      video
    } = props,
    rest = __rest(props, [
      'sizes',
      'fullScreenSizes',
      'thumbnailSizes',
      'aspect',
      'thumbnailPosition',
      'backgroundColor',
      'thumbnailQuality',
      'quality',
      'images',
      'galleryRef',
      'eagerlyLoad'
    ]);
  const {
    theme: { breakpoints }
  } = useShopConfig();
  const { selectedChannel } = React.useContext(ChannelContext);
  const baseUrl = (selectedChannel === null || selectedChannel === void 0
  ? void 0
  : selectedChannel.url)
    ? String(selectedChannel.url)
    : null;
  const [isFullscreen, setFullscreen] = React.useState(false);
  const galleryImages = React.useMemo(
    () =>
      constructGallery(images, fullScreenSizes, aspect, baseUrl, breakpoints),
    [aspect, baseUrl, breakpoints, fullScreenSizes, images]
  );
  if (video?.length > 0) {
    galleryImages.splice(1, 0, { video: true, videoUrl: video });
  }

  return React.createElement(
    'div',
    { className: wrapperStyles, style: { ['--slide-bg']: backgroundColor } },
    React.createElement(
      ImageGallery,
      Object.assign(
        {
          ref: r => (galleryRef ? (galleryRef.current = r) : null),
          thumbnailPosition: thumbnailPosition,
          items: galleryImages,
          autoPlay: false,
          showPlayButton: false,
          showThumbnails: galleryImages.length > 1,
          useBrowserFullscreen: false,
          preventDefaultTouchmoveEvent: isFullscreen,
          onScreenChange: el => setFullscreen(!!el),
          renderItem: item => {
            return (
              <>
                {item.video ? (
                  <VimeoVideo videoUrl={item.videoUrl} ratio={aspect} />
                ) : (
                  <div className="image-gallery-image">
                    {isFullscreen ? (
                      <img
                        src={item.original}
                        alt={item.originalAlt}
                        srcSet={item.srcSet}
                        sizes={item.sizes}
                        title={item.originalTitle}
                      />
                    ) : (
                      <React.Fragment>
                        <Image
                          src={item.original}
                          alt={item.originalAlt}
                          sizes={sizes}
                          title={item.originalTitle}
                          aspect={aspect}
                          quality={quality}
                        />
                        {props.children && <Overlay>{props.children}</Overlay>}
                      </React.Fragment>
                    )}
                  </div>
                )}
              </>
            );
          },
          renderThumbInner: item => {
            return (
              <>
                {item.video ? (
                  <div className="image-gallery-thumbnail-inner video-thumb">
                    <VideoIcon>
                      <Play />
                    </VideoIcon>
                    <Image
                      src={galleryImages[0].thumbnail}
                      alt={galleryImages[0].thumbnailAlt}
                      title={galleryImages[0].thumbnailTitle}
                      sizes={thumbnailSizes}
                      aspect={aspect}
                      quality={thumbnailQuality}
                    />
                  </div>
                ) : (
                  <div className="image-gallery-thumbnail-inner">
                    <Image
                      src={item.thumbnail}
                      alt={item.thumbnailAlt}
                      title={item.thumbnailTitle}
                      sizes={thumbnailSizes}
                      aspect={aspect}
                      quality={thumbnailQuality}
                    />
                    {item.thumbnailLabel && (
                      <div className="image-gallery-thumbnail-label">
                        {item.thumbnailLabel}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          }
        },
        rest
      )
    )
  );
};
